/**
 * Reveal Effect Presets
 *
 * Pre-configured settings for the radial reveal effect.
 * Users can choose from fast, medium, or slow presets.
 */

export interface RevealPresetConfig {
  /** Base wave speed in units/second */
  speed: number;
  /** Speed increase over time */
  acceleration: number;
  /** Time offset before lift wave starts (seconds) */
  delay: number;
  /** Position oscillation strength */
  oscillationIntensity: number;
  /** Additive color for initial dots */
  dotTint: { r: number; g: number; b: number };
  /** Additive color for lift wave highlight */
  waveTint: { r: number; g: number; b: number };
  /** Distance at which to disable effect for performance */
  endRadius: number;
}

/**
 * Reveal effect presets
 */
export const REVEAL_PRESETS: Record<string, RevealPresetConfig> = {
  /**
   * Fast preset - Quick reveal for shorter loading experiences
   * Duration: ~3-4 seconds for a 50 unit radius scene
   */
  fast: {
    speed: 40,
    acceleration: 8,
    delay: 0.1,
    oscillationIntensity: 0.1,
    dotTint: { r: 0, g: 1, b: 1 },      // Cyan
    waveTint: { r: 1, g: 0.5, b: 0 },   // Orange
    endRadius: 500
  },

  /**
   * Medium preset - Balanced reveal for typical scenes
   */
  medium: {
    speed: 20,
    acceleration: 4,
    delay: 0.5,
    oscillationIntensity: 0.2,
    dotTint: { r: 0, g: 1, b: 1 },      // Cyan
    waveTint: { r: 1, g: 0.5, b: 0 },   // Orange
    endRadius: 500
  },

  /**
   * Slow preset - Dramatic reveal for showcase/demo scenes
   */
  slow: {
    speed: 12,
    acceleration: 0,
    delay: 0.75,
    oscillationIntensity: 0.25,
    dotTint: { r: 0, g: 1, b: 1 },      // Cyan
    waveTint: { r: 1, g: 0.5, b: 0 },   // Orange
    endRadius: 500
  }
};

/**
 * Type for reveal effect preset names
 */
/**
 * Bloom burst reveal presets (faster, punchier)
 * Tuned for the bloom effect's expanding shockwave + overshoot settle.
 */
export const BLOOM_PRESETS: Record<string, RevealPresetConfig> = {
  fast: {
    speed: 32,
    acceleration: 100,
    delay: 0,
    oscillationIntensity: 0.15,
    dotTint: { r: 1, g: 0.6, b: 0.15 },     // Warm amber
    waveTint: { r: 0.2, g: 0.5, b: 1.0 },   // Cool blue
    endRadius: 500
  },
  medium: {
    speed: 20,
    acceleration: 72,
    delay: 0,
    oscillationIntensity: 0.2,
    dotTint: { r: 1, g: 0.6, b: 0.15 },
    waveTint: { r: 0.2, g: 0.5, b: 1.0 },
    endRadius: 500
  },
  slow: {
    speed: 12,
    acceleration: 40,
    delay: 0,
    oscillationIntensity: 0.25,
    dotTint: { r: 1, g: 0.6, b: 0.15 },
    waveTint: { r: 0.2, g: 0.5, b: 1.0 },
    endRadius: 500
  }
};

export type RevealPreset = 'fast' | 'medium' | 'slow' | 'none';
export type RevealStyle = 'bloom' | 'radial';

/**
 * Get preset configuration by name
 * Returns undefined for 'none' or invalid presets
 */
export function getRevealPreset(preset: RevealPreset, style: RevealStyle = 'bloom'): RevealPresetConfig | undefined {
  if (preset === 'none') {
    return undefined;
  }
  const table = style === 'bloom' ? BLOOM_PRESETS : REVEAL_PRESETS;
  return table[preset];
}
